import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

const ResourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const whitepapers = [
    { title: 'The Future of Research Automation: AI Agents in Social Science', description: 'How AI agents are transforming research methodologies.', pages: 24, date: '2024-01', topics: ['AI in Research', 'Methodology'] },
    { title: 'Cost-Benefit Analysis of Automated Research Workflows', description: 'Detailed economic analysis showing ROI and cost savings.', pages: 18, date: '2024-02', topics: ['ROI Analysis', 'Efficiency'] },
    { title: 'Best Practices for KoboToolbox Integration', description: 'Technical guide for implementing automated KoboToolbox workflows.', pages: 32, date: '2024-03', topics: ['KoboToolbox', 'Integration'] },
    { title: 'Quality Assurance in Automated Data Collection', description: 'Framework for ensuring data quality in AI-driven workflows.', pages: 28, date: '2024-03', topics: ['Data Quality', 'QA Framework'] }
  ];

  const blogPosts = [
    { title: 'How AI Agents Are Revolutionizing Research Methodology', excerpt: 'Explore the transformative impact of AI agents on traditional research approaches.', author: 'Dr. Sarah Chen', date: '2024-03-28', readTime: '8 min', category: 'AI & Research', featured: true },
    { title: '5 Common Mistakes in Survey Design and How AI Can Help', excerpt: 'Learn about the most frequent survey design pitfalls.', author: 'Michael Rodriguez', date: '2024-03-25', readTime: '6 min', category: 'Survey Design', featured: false },
    { title: 'The Economics of Research Automation: A Deep Dive', excerpt: 'Breaking down the cost savings and efficiency gains.', author: 'Dr. James Wilson', date: '2024-03-22', readTime: '10 min', category: 'Economics', featured: false },
    { title: 'Real-Time Data Quality Monitoring: Best Practices', excerpt: 'Strategies for maintaining high data quality standards.', author: 'Lisa Thompson', date: '2024-03-20', readTime: '7 min', category: 'Data Quality', featured: false }
  ];

  const documentation = [
    { title: 'Getting Started Guide', description: 'Complete guide to setting up your first project.', type: 'Guide', icon: 'pi pi-book' },
    { title: 'API Reference', description: 'Comprehensive API documentation.', type: 'Technical', icon: 'pi pi-code' },
    { title: 'KoboToolbox Integration', description: 'Step-by-step connection instructions.', type: 'Integration', icon: 'pi pi-link' },
    { title: 'Troubleshooting Guide', description: 'Common issues and solutions.', type: 'Support', icon: 'pi pi-question-circle' },
    { title: 'Security & Compliance', description: 'Data security and privacy features.', type: 'Security', icon: 'pi pi-shield' },
    { title: 'Video Tutorials', description: 'Step-by-step video guides.', type: 'Video', icon: 'pi pi-video' }
  ];

  const tabs = ['Whitepapers', 'Blog', 'Documentation'];
  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };

  return (
    <div>
      {/* Hero */}
      <div style={{ padding: '3rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem 0', color: '#1f2937' }}>Resources & Knowledge Center</h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: '700px', margin: '0 auto' }}>
          Explore whitepapers, guides, and insights to maximize the value of automated research workflows.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ padding: '2rem', backgroundColor: 'white' }}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
          {tabs.map((tab, i) => (
            <button key={i} onClick={() => setActiveTab(i)} style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, backgroundColor: activeTab === i ? '#3B82F6' : 'transparent', color: activeTab === i ? 'white' : '#6b7280' }}>
              {tab}
            </button>
          ))}
        </div>

        {/* Whitepapers Tab */}
        {activeTab === 0 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Research & Insights</h2>
              <p style={{ color: '#6b7280' }}>In-depth analysis on research automation and methodology</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              {whitepapers.map((paper, i) => (
                <div key={i} style={cardStyle}>
                  <h3 style={{ fontSize: '1.125rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{paper.title}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>{paper.description}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{paper.pages} pages • {paper.date}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    {paper.topics.map((topic, j) => (
                      <span key={j} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', backgroundColor: '#f3f4f6', borderRadius: '4px', color: '#6b7280' }}>{topic}</span>
                    ))}
                  </div>
                  <Button label="Download PDF" icon="pi pi-download" className="p-button-outlined p-button-sm" style={{ width: '100%' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === 1 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Latest Insights & Updates</h2>
              <p style={{ color: '#6b7280' }}>Stay up-to-date with trends and tips in research automation</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              {blogPosts.map((post, i) => (
                <div key={i} style={{ ...cardStyle, border: post.featured ? '2px solid #3B82F6' : 'none' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {post.featured && <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', backgroundColor: '#fef3c7', borderRadius: '4px', color: '#92400e' }}>Featured</span>}
                    <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', backgroundColor: '#dbeafe', borderRadius: '4px', color: '#1e40af' }}>{post.category}</span>
                  </div>
                  <h3 style={{ fontSize: '1.125rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{post.title}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1rem 0', lineHeight: 1.5 }}>{post.excerpt}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>By {post.author} • {post.readTime}</span>
                    <Button label="Read More" icon="pi pi-arrow-right" className="p-button-text p-button-sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documentation Tab */}
        {activeTab === 2 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Documentation & Guides</h2>
              <p style={{ color: '#6b7280' }}>Comprehensive documentation to help you get started</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              {documentation.map((doc, i) => (
                <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
                  <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '50%', backgroundColor: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto' }}>
                    <i className={doc.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i>
                  </div>
                  <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{doc.title}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.75rem 0' }}>{doc.description}</p>
                  <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', backgroundColor: '#f3f4f6', borderRadius: '4px', color: '#6b7280' }}>{doc.type}</span>
                  <div style={{ marginTop: '1rem' }}>
                    <Button label="View" icon="pi pi-external-link" className="p-button-outlined p-button-sm" style={{ width: '100%' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Newsletter */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Stay Updated</h2>
        <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Subscribe to our newsletter for the latest insights and updates.</p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', maxWidth: '400px', margin: '0 auto' }}>
          <input type="email" placeholder="Enter your email" style={{ flex: 1, padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '1rem' }} />
          <Button label="Subscribe" icon="pi pi-send" />
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#3B82F6', textAlign: 'center' }}>
        <h2 style={{ color: 'white', fontSize: '2rem', margin: '0 0 1rem 0' }}>Ready to Get Started?</h2>
        <p style={{ color: '#bfdbfe', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
          Join organizations already transforming their research operations.
        </p>
        <Button label="Contact Us" icon="pi pi-envelope" className="p-button-lg" style={{ backgroundColor: 'white', color: '#3B82F6', border: 'none' }} onClick={() => navigate('/contact')} />
      </div>
    </div>
  );
};

export default ResourcesPage;
